import {
  Box,
  Divider,
  Flex,
  IconButton,
  InputDescription,
  InputLabel,
  MultiSelectMenu,
  OptionType,
  Popover,
  Text,
  TextArea,
  TextInput,
  useSelectMenu,
} from '@fullstory/components';
import { IssuesBanner } from '@fullstory/dqm-ui';
import { intl } from '@fullstory/intl';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { Classification } from '@fullstory/proto/fs/services/search-settings';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import { assertExhaustive } from '@fullstory/typeutils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAiElementLabelSuggestions } from '../../../hooks/useAiElementLabelSuggestion';
import { NamedElementConfig, PlatformSelectorConfig } from '../../../types';
import { AISuggestedElementBadge } from '../../AISuggestedElementBadge';
import { ClassificationRuleSelect } from '../../ClassificationRuleSelect';
import {
  MultiPlatformSelectorInputs,
  SelectorInputSource,
} from '../../MultiPlatformSelectorInputs';
import { DataCaptureOptions } from '../DataCaptureOptions';
import { NamedElementModalVariant } from '../types';
import { NamedElementDetailsModalProps } from './types';

const WEB_ONLY_PLATFORMS = [NativePlatform.NATIVE_PLATFORM_WEB];

const selectorObjectType = intl.formatMessage({
  defaultMessage: 'element',
  id: 'HIhtA5',
  description: 'An element in Fullstory',
});

type ModalBodyDetailsProps = Omit<
  NamedElementDetailsModalProps,
  | 'onSave'
  | 'onUnarchive'
  | 'onArchive'
  | 'source'
  | 'properties'
  | 'auditHistory'
  | 'propertiesLoading'
  | 'propertiesFailed'
  | 'retryFailedProperties'
  | 'elementPropertyRules'
  | 'onCloseSignal'
  | 'param'
  | 'setParam'
  | 'objectReferences'
  | 'timeRange'
  | 'onTimeRangeChange'
  | 'activityMonitoring'
  | 'activityMonitoringError'
> & {
  selectedFsSpecObjectSlugs: OptionType<string>[];
  onChangeSelectedFsSpecSlugs: (fsSpecObjectOptions: OptionType<string>[]) => void;
  selectedClassification?: Classification | null;
  onClassificationChange?: (classification: Classification | null) => void;
  classificationOptions: OptionType<Classification>[];
  loadingClassification: boolean;
  fsSpecEnabled: boolean;
  fsSpecRoleOptions: OptionType<string>[];
  selectorFinding?: boolean;
  onHoverSelector: (selector?: string) => void;
  trimTrigger?: boolean;
  setTrimTrigger?: (off: boolean) => void;
  preventOverflow?: boolean;
  isSuggestion?: boolean;
};

export const ModalBodyDetails: React.FC<ModalBodyDetailsProps> = ({
  details,
  setDetails,
  issues,
  snoozed,
  setSnooze,
  variant,
  disableEdit,
  selectedFsSpecObjectSlugs,
  onChangeSelectedFsSpecSlugs,
  selectedClassification,
  onClassificationChange,
  classificationOptions,
  loadingClassification,
  fsSpecEnabled,
  fsSpecRoleOptions,
  validation,
  selectorFinding,
  savedApiName,
  onHoverSelector,
  trimTrigger,
  setTrimTrigger,
  preventOverflow,
  isSuggestion,
}) => {
  const flags = useFlags();
  const intl = useIntl();
  const webOnly = !flags.hasBit(Flag.FeatNativeMobile);
  const watchedElemsEnabled = flags.hasBit(Flag.FeatWatchedElems);
  const autoWatchEnabled = flags.featureFlags().AppAutoWatchErrorAndLoadingIndicators;
  const classificationRulesEnabled = flags.featureFlags().AppClassificationRules;

  const namedElementRecord = details.namedElement!;
  const namedElement = namedElementRecord.namedElement!;
  const [nameSuggestionsOpen, setNameSuggestionsOpen] = useState(false);

  // keep validation as local state so that we can clear when the corresponding fields are focused
  const [nameValidation, setNameValidation] = useState(validation?.name);
  // but we will still update the validation state when the caller passes new validation in
  useEffect(() => {
    setNameValidation(validation?.name);
  }, [validation?.name]);

  // name options get filtered as the user types, and should not show already used spec objects
  const nameOptions = useMemo(
    () =>
      fsSpecRoleOptions.filter(
        option =>
          option.label.toLocaleLowerCase().includes(namedElement.name.toLocaleLowerCase()) &&
          !option.disabled,
      ),
    [fsSpecRoleOptions, namedElement.name],
  );

  const {
    suggestions: aiNameOptions,
    loadSuggestions,
    isEnabled: aiLabelsEnabled,
    loading,
  } = useAiElementLabelSuggestions({
    selector:
      details.namedElement?.namedElement?.platformSelectors[0]?.selector ||
      namedElement.platformSelectors[0]?.canonicalizedSelector ||
      namedElement.platformSelectors[0]?.selector ||
      '',
  });

  const setNamedElement = useCallback(
    (namedElement: NamedElementConfig) => {
      if (details && details.namedElement) {
        setDetails({ ...details, namedElement: { ...details.namedElement, namedElement } });
      }
    },
    [details, setDetails],
  );

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  // select menu for name suggestions
  const { Options, optionsProps } = useSelectMenu({
    testId: 'element-modal-name-suggestions',
    options: nameOptions,
    preventOverflow: preventOverflow ? 16 : undefined,
    flipY: preventOverflow || undefined,
    onChange: option => {
      if (option) {
        setNamedElement({ ...namedElement, name: option.label });
        onChangeSelectedFsSpecSlugs([option]);
        setNameSuggestionsOpen(false);
      }
    },
  });

  // select menu for AI name suggestions
  const { Options: OptionsAi, optionsProps: optionsPropsAi } = useSelectMenu({
    testId: 'element-modal-name-suggestions-ai',
    options: aiNameOptions,
    onChange: option => {
      if (option && option.label) {
        if (option.value.specSlug) {
          onChangeSelectedFsSpecSlugs([
            {
              label: option.value.specSlug,
              value: option.value.specSlug,
              key: option.value.specSlug,
            },
          ]);
        }

        setNamedElement({
          ...namedElement,
          name: option.value.name,
          description: option.value.description,
        });
        setNameSuggestionsOpen(false);
      }
    },
  });

  const setWatched = useCallback(
    (watched: boolean) => {
      if (details && details.namedElement && details.watched !== watched) {
        setDetails({ ...details, watched });
      }
    },
    [details, setDetails],
  );

  const setRageClicksBlocked = useCallback(
    (ignoreRageClicks: boolean) => {
      if (details && details.namedElement) {
        setDetails({ ...details, ignoreRageClicks });
      }
    },
    [details, setDetails],
  );

  const setDeadClicksBlocked = useCallback(
    (ignoreDeadClicks: boolean) => {
      if (details && details.namedElement) {
        setDetails({ ...details, ignoreDeadClicks });
      }
    },
    [details, setDetails],
  );

  useEffect(() => {
    // Set default watched to true for autowatched suggestions
    const disabled = disableEdit || namedElementRecord.metadata?.archived;
    const isAutowatchedSuggestion =
      isSuggestion && details.namedElement?.namedElement?.suggestion?.autoWatch;
    if (autoWatchEnabled && isAutowatchedSuggestion && !disabled && watchedElemsEnabled) {
      setWatched(true);
    }
  }, [
    details.namedElement?.namedElement?.suggestion?.autoWatch,
    disableEdit,
    isSuggestion,
    namedElementRecord.metadata?.archived,
    watchedElemsEnabled,
    autoWatchEnabled,
  ]);

  const platformSelectors: PlatformSelectorConfig[] = useMemo(() => {
    if (namedElement.platformSelectors.length === 0) {
      // Create a skeleton platform selector
      return [{ selector: '', originalSelector: '', canonicalizedSelector: null, platforms: [] }];
    }

    if (webOnly) {
      // If the organization only has web enabled, make sure all selectors are web.
      return namedElement.platformSelectors.map(ps => ({
        ...ps,
        platforms: WEB_ONLY_PLATFORMS,
      }));
    }

    return namedElement.platformSelectors;
  }, [namedElement.platformSelectors, webOnly]);

  const nameFocusRef = useRef<HTMLInputElement | null>(null);
  const selectorFocusRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    // auto focus only for new elements
    if (!namedElement.id) {
      if (selectorFinding) {
        // when selector focused, focus on the first selector
        selectorFocusRef.current?.focus();
      } else {
        // otherwise focus on the name
        nameFocusRef.current?.focus();
      }
    }
  }, []);

  const header = useMemo(() => {
    if (!selectorFinding) {
      return variant != NamedElementModalVariant.new ? (
        <FormattedMessage defaultMessage="Details" id="Lv0zJu" />
      ) : undefined;
    }
    switch (variant) {
      case NamedElementModalVariant.new:
        return <FormattedMessage defaultMessage="Create Element" id="TWGQeP" />;
      case NamedElementModalVariant.edit:
        return <FormattedMessage defaultMessage="Edit Element" id="NMcgWE" />;
      case NamedElementModalVariant.view:
        return <FormattedMessage defaultMessage="View Element" id="LkELXm" />;
      default:
        assertExhaustive(variant);
    }
  }, [selectorFinding, variant]);

  const Selectors = (
    <Box marginBottom={32}>
      <Box marginBottom={8}>
        <Text appearance="bodyMedium" color="emphasized">
          <FormattedMessage defaultMessage="Element Definition" id="p0Omyq" />
        </Text>
        {selectorFinding && (
          <Text appearance="bodyMedium" color="secondary">
            <FormattedMessage defaultMessage="Select from preview or type selector" id="KeDDI/" />
          </Text>
        )}
      </Box>
      <MultiPlatformSelectorInputs
        firstSelectorRef={selectorFocusRef}
        disabled={disableEdit || namedElementRecord.metadata?.archived}
        apiName={namedElement.apiName}
        savedApiName={savedApiName}
        selectors={platformSelectors}
        validationStates={validation?.platformSelectors || []}
        onChange={(platformSelectors, apiName) =>
          setNamedElement({ ...namedElement, apiName, platformSelectors })
        }
        onHoverSelector={onHoverSelector}
        source={SelectorInputSource.elementModal}
        trimTrigger={trimTrigger}
        setTrimTrigger={setTrimTrigger}
      />
    </Box>
  );

  return (
    <Box
      data-component="ModalBodyDetails"
      testId="named-element-details"
      paddingBottom={selectorFinding ? 16 : 0}
    >
      {header && (
        <Box marginBottom={20}>
          <Text appearance="heading1">{header}</Text>
          {isSuggestion && (
            <Box marginTop={8}>
              <AISuggestedElementBadge />
            </Box>
          )}
        </Box>
      )}
      {namedElementRecord.namedElement?.id &&
        (issues.length > 0 || snoozed) &&
        !namedElementRecord.metadata?.archived && (
          <Box marginBottom={8}>
            <IssuesBanner
              snoozed={snoozed || null}
              setSnooze={setSnooze}
              objectType={selectorObjectType}
            />
          </Box>
        )}
      <Box
        display="flex"
        flexDirection={
          selectorFinding && variant === NamedElementModalVariant.new ? 'column' : 'column-reverse'
        }
      >
        {Selectors}
        <Box marginBottom={32}>
          <Box marginBottom={16}>
            <Flex justifyContent="flex-start" alignItems="baseline">
              <InputLabel id="name">
                <FormattedMessage defaultMessage="Name" id="HAlOn1" />
              </InputLabel>
            </Flex>
            <Box ref={setPopoverAnchor}>
              <TextInput
                ref={nameFocusRef}
                onChange={e => {
                  setNamedElement({ ...namedElement, name: e.target.value });
                }}
                value={namedElement.name}
                placeholder={intl.formatMessage({
                  defaultMessage: 'e.g. Checkout Button',
                  id: '8fe7R5',
                })}
                disabled={disableEdit || namedElementRecord.metadata?.archived}
                invalid={!!nameValidation?.invalid}
                aria-labelledby="name"
                testId="named-element-modal-name-input"
                // automatically open name suggestions only if there's no name and there's a selector (ie. inspect mode)
                onFocus={() => {
                  setNameValidation(undefined);
                  if (
                    !namedElement.name &&
                    platformSelectors.length > 0 &&
                    platformSelectors[0]!.selector.length > 0 &&
                    fsSpecEnabled
                  )
                    setNameSuggestionsOpen(true);
                }}
                rightAdornment={
                  platformSelectors[0]?.selector?.length && aiLabelsEnabled ? (
                    <IconButton
                      iconColor="ai"
                      size="large"
                      title={intl.formatMessage({ defaultMessage: 'Suggest name', id: 'KHtUXF' })}
                      iconName={loading ? 'ai-animated' : 'ai'}
                      onClick={() => {
                        loadSuggestions();
                        setNameSuggestionsOpen(true);
                      }}
                    />
                  ) : undefined
                }
              />
            </Box>
            <Popover
              anchor={popoverAnchor}
              onCloseSignal={() => setNameSuggestionsOpen(false)}
              open={nameSuggestionsOpen}
              variant="bordered"
              testId="named-element-modal-name-suggestions"
            >
              <Box maxHeight={385} overflow="auto">
                {aiLabelsEnabled && (
                  <>
                    <OptionsAi {...optionsPropsAi} />
                    <Divider />
                  </>
                )}
                <Options {...optionsProps} />
              </Box>
            </Popover>
            {!!nameValidation?.invalid && (
              <InputDescription invalid description={nameValidation?.invalid?.message} />
            )}
          </Box>
          {fsSpecEnabled && fsSpecRoleOptions.length > 0 && (
            <Box marginBottom={16} testId="fs-spec-role-select">
              <InputLabel id="Role" optional>
                <FormattedMessage defaultMessage="Role" id="1ZgrhW" />
              </InputLabel>
              <MultiSelectMenu
                menuHeight={385}
                variant="fill-builder"
                options={fsSpecRoleOptions}
                onChange={onChangeSelectedFsSpecSlugs}
                selected={selectedFsSpecObjectSlugs}
                filterable
                testId="named-element-modal-role-multi-select"
                preventOverflow={preventOverflow ? 16 : undefined}
                flipY={preventOverflow || undefined}
              />
            </Box>
          )}
          <Box marginBottom={16}>
            {classificationRulesEnabled && onClassificationChange && (
              <ClassificationRuleSelect
                elementId={namedElement.id}
                classificationOptions={classificationOptions}
                loadingClassification={loadingClassification}
                selectedClassification={selectedClassification}
                onClassificationChange={onClassificationChange}
                disabled={disableEdit || namedElementRecord.metadata?.archived}
                preventOverflow={preventOverflow}
              />
            )}
          </Box>
          <Box>
            <Flex justifyContent="flex-start" alignItems="baseline">
              <InputLabel id="description" optional>
                <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
              </InputLabel>
            </Flex>
            <TextArea
              onChange={e => {
                setNamedElement({ ...namedElement, description: e.target.value });
              }}
              value={namedElement.description}
              placeholder={intl.formatMessage({
                defaultMessage: 'Add a short description for this Element...',
                id: 'Ceq58n',
              })}
              disabled={disableEdit || namedElementRecord.metadata?.archived}
              aria-labelledby="description"
            />
          </Box>
        </Box>
      </Box>
      <DataCaptureOptions
        watched={details.watched}
        setWatched={setWatched}
        rageClicksBlocked={details.ignoreRageClicks}
        setRageClicksBlocked={setRageClicksBlocked}
        deadClicksBlocked={details.ignoreDeadClicks}
        setDeadClicksBlocked={setDeadClicksBlocked}
        disabled={disableEdit || namedElementRecord.metadata?.archived}
        autoWatch={
          autoWatchEnabled &&
          isSuggestion &&
          details.namedElement?.namedElement?.suggestion?.autoWatch
        }
      />
    </Box>
  );
};
